/**
 * The honest refusal (web-pre-deploy item 6). Rendered by `routes.tsx`'s
 * `gateArea` when a principal reaches an area whose capability it does not
 * hold — a bookmark that used to work, a link someone else sent, a guessed
 * or typed URL, a browser autocompleting an address from before a permission
 * changed.
 *
 * A SCREEN, deliberately, not a `<Navigate to={landingFor(me)} />`. The
 * redirect is the tempting version — it always lands somewhere that works,
 * and it never shows anyone an error. That is exactly its problem: the user
 * asked for one thing, silently got another, and has no way to tell whether
 * the bookmark is stale, whether their permissions changed, or whether the
 * app is simply broken. The redirect also destroys the only evidence — the
 * URL they came in on — before anyone can look at it. `test/route-gate
 * .test.tsx`'s "answers on the spot rather than redirecting" is the covering
 * test, and the redirect is the mutation it catches.
 *
 * NO capability name, NO route path, NO developer identifier (CLAUDE.md; a
 * rule this delivery cycle has already had to fix on two other screens).
 * "commercial", "costs-entry" and "/money" are words from this repo's
 * vocabulary, not a site manager's. What a user CAN act on is the area's own
 * nav LABEL — the identical string the nav link would have carried had they
 * held the capability — so that is what `area` is, and it is passed in by
 * `gateArea` straight from the `NAV_ITEMS` row that gates the route.
 *
 * The second sentence is worded to stay true for a principal with NO areas
 * at all (nav empty): "any areas your account can open are listed" is honest
 * about an empty list, where "choose another area from the menu" would be
 * pointing at nothing. `AppShell`'s `NoAreasScreen` still owns the case
 * where such a principal arrives at `/` with nowhere to land; this is the
 * narrower "you asked for THIS one" case.
 *
 * Styling: theme tokens only, and the same two-element shape as its
 * neighbours `NoAreasScreen` and `NotFoundScreen` (`shell/AppShell.tsx`) —
 * `text-muted-foreground` and the type scale, never a colour. jsdom computes
 * no styles, so this file's token contract is Playwright's to assert, not
 * this layer's (`docs/frontend-testing.md`, "What this layer does NOT
 * cover").
 */
export function NoAccessScreen({ area }: { area: string }) {
  return (
    <div className="space-y-2">
      <h1 className="text-xl font-semibold">You do not have access to this area</h1>
      <p className="text-sm text-muted-foreground">
        Your account does not have access to {area}. Any areas your account can open are listed in the
        navigation; ask an administrator if you need this one.
      </p>
    </div>
  )
}
